<template>
  <div
    class="layout--main"
    :class="[
      layoutTypeClass,
      navbarClasses,
      footerClasses,
      { 'no-scroll': isAppPage },
    ]"
  >
    <vx-tour
      :steps="steps"
      v-if="
        !disableThemeTour &&
        windowWidth >= 1200 &&
        mainLayoutType === 'vertical' &&
        verticalNavMenuWidth == 'default'
      "
    />

    <!-- Loading Placeholder for Menu -->
    <div v-if="!tenantDataLoaded" class="v-nav-menu-loading">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-message">Loading menu...</p>
      </div>
    </div>

    <v-nav-menu
      v-else
      :navMenuItems="navMenuItems"
      title="Vuexy"
      parent=".layout--main"
    />

    <div
      id="content-area"
      :class="[contentAreaClass, { 'show-overlay': bodyOverlay }]"
    >
      <div id="content-overlay" />

      <!-- Navbar -->
      <template v-if="mainLayoutType === 'horizontal' && windowWidth >= 1200">
        <the-navbar-horizontal
          :navbarType="navbarType"
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark },
          ]"
        />

        <div style="height: 62px" v-if="navbarType === 'static'"></div>

        <!-- Horizontal menu also waits for tenant data -->
        <div v-if="!tenantDataLoaded" class="h-nav-menu-loading">
          <div class="loading-text">Loading menu...</div>
        </div>
        <h-nav-menu
          v-else
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark },
          ]"
          :navMenuItems="navMenuItems"
        />
      </template>

      <template v-else>
        <the-navbar-vertical
          :navbarColor="navbarColor"
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark },
          ]"
        />
      </template>
      <!-- /Navbar -->

      <div class="content-wrapper">
        <div class="router-view">
          <div class="router-content">
            <!-- Read-only banner: shown when the session was opened via the SmartInvoice
                 Console "Open in SmartInvoice" hand-off. Writes are blocked server-side. -->
            <div v-if="isReadOnlySession" class="readonly-session-banner">
              <feather-icon icon="EyeIcon" svgClasses="h-4 w-4 mr-2 align-middle" />
              <span>
                Read-only view — opened from SmartInvoice Console. You can browse this
                account, but changes are disabled.
              </span>
            </div>
            <transition :name="routerTransition">
              <div
                v-if="$route.meta.breadcrumb || $route.meta.pageTitle"
                class="flex flex-wrap items-center mb-6 router-header"
              >
                <div
                  class="content-area__heading"
                  :class="{
                    'pr-4 border-0 md:border-r border-solid border-grey-light':
                      $route.meta.breadcrumb,
                  }"
                >
                  <h2 class="mb-1">{{ translatedRouteTitle }}</h2>
                </div>

                <!-- BREADCRUMB -->
                <vx-breadcrumb
                  class="hidden ml-4 md:block"
                  v-if="$route.meta.breadcrumb"
                  :route="$route"
                  :isRTL="$vs.rtl"
                />
              </div>
            </transition>

            <div class="content-area__content">
              <back-to-top
                bottom="5%"
                :right="$vs.rtl ? 'calc(100% - 2.2rem - 38px)' : '30px'"
                visibleoffset="500"
                v-if="!hideScrollToTop"
              >
                <vs-button
                  icon-pack="feather"
                  icon="icon-arrow-up"
                  class="shadow-lg btn-back-to-top"
                />
              </back-to-top>

              <transition :name="routerTransition" mode="out-in">
                <router-view
                  @changeRouteTitle="changeRouteTitle"
                  @setAppClasses="
                    (classesStr) => $emit('setAppClasses', classesStr)
                  "
                />
              </transition>
            </div>
          </div>
        </div>
      </div>
      <the-footer />
    </div>
  </div>
</template>

<script>
import BackToTop from 'vue-backtotop'
import HNavMenu from '@/layouts/components/horizontal-nav-menu/HorizontalNavMenu.vue'
import TheNavbarHorizontal from '@/layouts/components/navbar/TheNavbarHorizontal.vue'
import TheNavbarVertical from '@/layouts/components/navbar/TheNavbarVertical.vue'
import TheFooter from '@/layouts/components/TheFooter.vue'
import themeConfig from '@/../themeConfig.js'
import VNavMenu from '@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue'

import store from '@/store/store'
import navMenuConfig from '@/config/navMenuConfig.js'
import { filterMenuByEnabledSlugs } from '@/utils/menuFilter.js'

const VxTour = () => import('@/components/VxTour.vue')

export default {
  components: {
    BackToTop,
    HNavMenu,
    TheFooter,
    TheNavbarHorizontal,
    TheNavbarVertical,
    VNavMenu,
    VxTour,
  },
  data() {
    return {
      disableCustomizer: themeConfig.disableCustomizer,
      disableThemeTour: themeConfig.disableThemeTour,
      dynamicWatchers: {},
      footerType: themeConfig.footerType || 'static',
      hideScrollToTop: themeConfig.hideScrollToTop,
      isNavbarDark: false,
      navbarColor: themeConfig.navbarColor || '#fff',
      navbarType: themeConfig.navbarType || 'floating',
      routerTransition: themeConfig.routerTransition || 'none',
      routeTitle: this.$route.meta.pageTitle,
      tenantDataLoaded: false,
      steps: [
        {
          target: '#btnVNavMenuMinToggler',
          content: 'Toggle Collapse Sidebar.',
        },
        {
          target: '.vx-navbar__starred-pages',
          content:
            'Create your own bookmarks. You can also re-arrange them using drag & drop.',
        },
        {
          target: '.i18n-locale',
          content: 'You can change language from here.',
        },
        {
          target: '.navbar-fuzzy-search',
          content: 'Try fuzzy search to visit pages in flash.',
        },
        {
          target: '.customizer-btn',
          content: 'Customize template based on your preference',
          params: {
            placement: 'left',
          },
        },
        {
          target: '.vs-button.buy-now',
          content: 'Buy this awesomeness at affordable price!',
          params: {
            placement: 'top',
          },
        },
      ],
    }
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        this.routeTitle = this.$route.meta.pageTitle
      })
    },
    isThemeDark(val) {
      const color = this.navbarColor === '#fff' && val ? '#10163a' : '#fff'
      this.updateNavbarColor(color)
    },
    '$store.state.mainLayoutType'(val) {
      this.setNavMenuVisibility(val)
      this.disableThemeTour = true
    },
  },
  computed: {
    navMenuItems() {
      const enabledSlugs = this.$store.state.AppActiveTenant?.theme?.enabledMenus
      const tenantSettings = this.$store.state.AppActiveTenant
      const filteredMenu = filterMenuByEnabledSlugs(navMenuConfig, enabledSlugs, tenantSettings)
      // Update store for any other components that might need it
      store.commit('SET_NAV_MENU', filteredMenu)
      return filteredMenu
    },
    translatedRouteTitle() {
      const title = this.routeTitle
      if (!title) return ''
      const i18nKey = this.$route.meta.i18nKey
      if (i18nKey && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }
      const pageTitleKey = `pageTitle.${title.toLowerCase().replace(/[\s-]+/g, '')}`
      if (this.$te(pageTitleKey)) {
        return this.$t(pageTitleKey)
      }
      return title
    },
    bodyOverlay() {
      return this.$store.state.bodyOverlay
    },
    contentAreaClass() {
      if (this.mainLayoutType === 'vertical') {
        if (this.verticalNavMenuWidth === 'default')
          return 'content-area-reduced'
        else if (this.verticalNavMenuWidth === 'reduced')
          return 'content-area-lg'
        else return 'content-area-full'
      } else return 'content-area-full'
    },
    footerClasses() {
      return {
        'footer-hidden': this.footerType === 'hidden',
        'footer-sticky': this.footerType === 'sticky',
        'footer-static': this.footerType === 'static',
      }
    },
    isAppPage() {
      return this.$route.meta.no_scroll
    },
    isReadOnlySession() {
      return localStorage.getItem('readOnlySession') === 'true'
    },
    isThemeDark() {
      return this.$store.state.theme === 'dark'
    },
    layoutTypeClass() {
      return `main-${this.mainLayoutType}`
    },
    mainLayoutType() {
      return this.$store.state.mainLayoutType
    },
    navbarClasses() {
      return {
        'navbar-hidden': this.navbarType === 'hidden',
        'navbar-sticky': this.navbarType === 'sticky',
        'navbar-static': this.navbarType === 'static',
        'navbar-floating': this.navbarType === 'floating',
      }
    },
    verticalNavMenuWidth() {
      return this.$store.state.verticalNavMenuWidth
    },
    windowWidth() {
      return this.$store.state.windowWidth
    },
  },
  methods: {
    changeRouteTitle(title) {
      this.routeTitle = title
    },
    updateNavbar(val) {
      if (val === 'static')
        this.updateNavbarColor(this.isThemeDark ? '#10163a' : '#fff')
      this.navbarType = val
    },
    updateNavbarColor(val) {
      this.navbarColor = val
      if (val === '#fff') this.isNavbarDark = false
      else this.isNavbarDark = true
    },
    updateFooter(val) {
      this.footerType = val
    },
    updateRouterTransition(val) {
      this.routerTransition = val
    },
    setNavMenuVisibility(layoutType) {
      if (
        (layoutType === 'horizontal' && this.windowWidth >= 1200) ||
        (layoutType === 'vertical' && this.windowWidth < 1200)
      ) {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false)
        this.$store.dispatch('updateVerticalNavMenuWidth', 'no-nav-menu')
      } else {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true)
      }
    },
    toggleHideScrollToTop(val) {
      this.hideScrollToTop = val
    },
    async loadTenantData() {
      // Set a timeout to prevent infinite loading
      const timeout = setTimeout(() => {
        if (!this.tenantDataLoaded) {
          console.warn('Tenant data loading timeout - using cached data')
          this.tenantDataLoaded = true
        }
      }, 5000) // 5 second timeout

      try {
        // Check if user is authenticated before making API call
        if (!localStorage.getItem('accessToken')) {
          console.warn('No access token found, redirecting to login')
          this.$router.push('/login')
          clearTimeout(timeout)
          return
        }

        const { data: tenantData } = await this.$http.get('tenants/activeTenant')
        if (tenantData) {
          this.$store.commit('SET_ACTIVE_TENANT', tenantData)
        }
      } catch (error) {
        console.error('Failed to load tenant data:', error)
        
        // If it's an auth error, redirect to login
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          console.warn('Authentication error, redirecting to login')
          this.$router.push('/login')
        }
        // Otherwise, use cached data from localStorage
      } finally {
        clearTimeout(timeout)
        this.tenantDataLoaded = true
      }
    },
  },
  async created() {
    const color =
      this.navbarColor === '#fff' && this.isThemeDark
        ? '#10163a'
        : this.navbarColor
    this.updateNavbarColor(color)
    this.setNavMenuVisibility(this.$store.state.mainLayoutType)

    // Fetch tenant data to ensure menu accuracy
    // Don't block the entire app if this fails
    this.loadTenantData().catch((err) => {
      console.error('Critical error loading tenant data:', err)
      this.tenantDataLoaded = true // Ensure UI loads even on critical error
    })

    // Dynamic Watchers for tour
    // Reason: Once tour is disabled it is not required to enable it.
    // So, watcher is required for just disabling it.
    this.dynamicWatchers.windowWidth = this.$watch(
      '$store.state.windowWidth',
      (val) => {
        if (val < 1200) {
          this.disableThemeTour = true
          this.dynamicWatchers.windowWidth()
        }
      }
    )

    this.dynamicWatchers.verticalNavMenuWidth = this.$watch(
      '$store.state.verticalNavMenuWidth',
      () => {
        this.disableThemeTour = true
        this.dynamicWatchers.verticalNavMenuWidth()
      }
    )

    this.dynamicWatchers.rtl = this.$watch('$vs.rtl', () => {
      this.disableThemeTour = true
      this.dynamicWatchers.rtl()
    })
  },
  mounted() {
    // Additional safety: If data isn't loaded after 10 seconds in mounted state, force load
    setTimeout(() => {
      if (!this.tenantDataLoaded) {
        console.error('Tenant data loading failed after 10 seconds - forcing menu display')
        this.tenantDataLoaded = true
      }
    }, 10000)
  },
  beforeDestroy() {
    Object.keys(this.dynamicWatchers).map((i) => {
      this.dynamicWatchers[i]()
      delete this.dynamicWatchers[i]
    })
  },
}
</script>

<style scoped>
/* Read-only session banner (SmartInvoice Console hand-off) */
.readonly-session-banner {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #f0b429;
  background-color: #fdf6e3;
  color: #8a6d0b;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Loading state for vertical nav menu */
.v-nav-menu-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 260px;
  height: 100vh;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50000;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #7367f0;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-message {
  margin: 0;
  color: #626262;
  font-size: 0.875rem;
  text-align: center;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Loading state for horizontal nav menu */
.h-nav-menu-loading {
  padding: 1rem 2rem;
  text-align: center;
}

.loading-text {
  color: #626262;
  font-size: 0.875rem;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* Dark theme support */
.theme-dark .v-nav-menu-loading {
  background-color: #262c49;
}

.theme-dark .loading-text {
  color: #b8c2cc;
}
</style>
